import React, { createContext, useState, ReactNode, useMemo } from 'react';
import { useRobloxAuthentication } from '@rbx/auth';
import TryItOutAuthMode from '@modules/openCloud/swagger/types/TryItOutAuthMode';
import RiskLevel from '@modules/openCloud/swagger/types/RiskLevel';
import useOperation from '../hooks/useOperation';

export interface TryItOutContextValue {
  tryItOutAllowed: boolean;
  tryItOutOpen: boolean;
  setTryItOutOpen: (open: boolean) => void;

  showTryItOutWarning: boolean;
  setWarningAccepted: (accepted: boolean) => void;

  authMode: TryItOutAuthMode;
  setAuthMode: (mode: TryItOutAuthMode) => void;

  showExecutionWarning: boolean;
  setExecutionWarningAccepted: (accepted: boolean) => void;
}

export interface TryItOutProviderProps {
  children: ReactNode;
}

export const TryItOutContext = createContext<TryItOutContextValue | null>(null);

export const TryItOutProvider: React.FC<TryItOutProviderProps> = ({ children }) => {
  const { status } = useRobloxAuthentication();
  const { riskLevel } = useOperation();

  const [authMode, setAuthMode] = useState<TryItOutAuthMode>(
    status === 'unauthenticated' ? TryItOutAuthMode.NONE : TryItOutAuthMode.COOKIE,
  );

  const [tryItOutOpen, setTryItOutOpen] = useState(false);
  const [warningAccepted, setWarningAccepted] = useState(false);
  const [executionWarningAccepted, setExecutionWarningAccepted] = useState(false);

  // Try it out is allowed if risk level is not critical
  const tryItOutAllowed = useMemo(() => riskLevel !== RiskLevel.CRITICAL, [riskLevel]);
  // Try it out warning is shown if risk level is not low and warning has not been accepted
  const showTryItOutWarning = useMemo(
    () => riskLevel !== RiskLevel.LOW && !warningAccepted,
    [riskLevel, warningAccepted],
  );
  // Execution warning is shown if risk level is high and execution warning has not been accepted
  const showExecutionWarning = useMemo(
    () => riskLevel === RiskLevel.HIGH && !executionWarningAccepted,
    [riskLevel, executionWarningAccepted],
  );

  const contextValue: TryItOutContextValue = useMemo(
    () => ({
      tryItOutAllowed,
      tryItOutOpen,
      setTryItOutOpen,

      showTryItOutWarning,
      setWarningAccepted,

      authMode,
      setAuthMode,

      showExecutionWarning,
      setExecutionWarningAccepted,
    }),
    [
      tryItOutAllowed,
      tryItOutOpen,
      setTryItOutOpen,

      showTryItOutWarning,
      setWarningAccepted,

      authMode,
      setAuthMode,

      showExecutionWarning,
      setExecutionWarningAccepted,
    ],
  );

  return <TryItOutContext.Provider value={contextValue}>{children}</TryItOutContext.Provider>;
};

export default TryItOutProvider;
